import { useCallback, useEffect, useState } from "react";
import { X, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { timeAgo } from "@/lib/timeAgo";

type Comment = {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  user: { username: string; display_name: string; avatar_url: string | null } | null;
};

export function CommentSheet({
  postId,
  open,
  onClose,
  onAdded,
}: {
  postId: string;
  open: boolean;
  onClose: () => void;
  onAdded?: () => void;
}) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("post_comments")
      .select("id, user_id, content, created_at, user:profiles(username, display_name, avatar_url)")
      .eq("post_id", postId)
      .order("created_at", { ascending: true });
    setComments((data ?? []) as unknown as Comment[]);
  }, [postId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const send = async () => {
    if (!user || !draft.trim()) return;
    setSending(true);
    const { error } = await supabase
      .from("post_comments")
      .insert({ post_id: postId, user_id: user.id, content: draft.trim() });
    setSending(false);
    if (!error) {
      setDraft("");
      await load();
      onAdded?.();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 animate-in fade-in duration-200"
      />
      <div
        className="relative w-full max-h-[80vh] flex flex-col animate-in slide-in-from-bottom duration-300"
        style={{
          backgroundColor: "var(--colour-surface)",
          borderTopLeftRadius: "var(--radius-lg)",
          borderTopRightRadius: "var(--radius-lg)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="pt-2.5 pb-1 flex justify-center shrink-0">
          <div
            className="w-10 h-1 rounded-full"
            style={{ backgroundColor: "var(--colour-border)" }}
          />
        </div>
        <div className="flex items-center justify-between px-4 h-12 shrink-0">
          <span className="w-11" />
          <h2 className="text-[16px] font-semibold">Comments</h2>
          <button
            aria-label="Close"
            onClick={onClose}
            className="w-11 h-11 -mr-3 flex items-center justify-center"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-2">
          {comments.length === 0 ? (
            <p className="text-center text-[14px] text-[var(--colour-text-secondary)] py-10">
              No comments yet. Be the first.
            </p>
          ) : (
            <ul className="space-y-3">
              {comments.map((c) => (
                <li key={c.id} className="flex gap-3">
                  <Avatar url={c.user?.avatar_url ?? null} name={c.user?.display_name ?? "?"} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[14px] font-semibold truncate">
                        {c.user?.display_name ?? "User"}
                      </span>
                      <span className="text-[12px] text-[var(--colour-text-secondary)]">
                        {timeAgo(c.created_at)}
                      </span>
                    </div>
                    <p className="text-[14px] mt-0.5 whitespace-pre-wrap break-words">
                      {c.content}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className="px-4 py-3 border-t flex items-center gap-2 shrink-0"
          style={{ borderColor: "var(--colour-border)" }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 500))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Add a comment..."
            className="flex-1 h-11 px-3 rounded-[var(--radius-md)] border text-[14px] outline-none"
            style={{
              borderColor: "var(--colour-border)",
              backgroundColor: "var(--colour-surface)",
              color: "var(--colour-text-primary)",
            }}
          />
          <button
            onClick={send}
            disabled={sending || !draft.trim()}
            className="w-11 h-11 rounded-[var(--radius-full)] flex items-center justify-center disabled:opacity-50"
            style={{
              backgroundColor: "var(--colour-accent)",
              color: "var(--colour-text-inverse)",
            }}
            aria-label="Send"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Avatar({ url, name }: { url: string | null; name: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name}
        className="w-9 h-9 rounded-[var(--radius-full)] object-cover shrink-0"
      />
    );
  }
  return (
    <div
      className="w-9 h-9 rounded-[var(--radius-full)] flex items-center justify-center text-[14px] font-semibold shrink-0"
      style={{
        backgroundColor: "var(--colour-surface-raised)",
        color: "var(--colour-text-secondary)",
      }}
    >
      {name.slice(0, 1).toUpperCase()}
    </div>
  );
}
